import type { Column, Table } from '@tanstack/react-table';
import { useMemo } from 'react';

type NumberItem = {
    id: string;
    label: string;
    value: number;
};

type UseNumberFilterDataProps<TData, TValue> = {
    disabled: boolean;
    table: Table<TData>;
    column: Column<TData, TValue>;
};

export function useNumberFilterData<TData, TValue>({
    disabled,
    table,
    column,
}: UseNumberFilterDataProps<TData, TValue>) {
    return useMemo(() => {
        if (disabled) {
            return {
                items: [],
                allIds: [],
            };
        }

        // get all values and create a set of unique numbers
        const valueSet = new Set<number>();

        table.options.data.forEach((row) => {
            const value = (row as Record<string, number | null | undefined>)[column.id];

            if (value === null || value === undefined) {
                return;
            }

            valueSet.add(value);
        });

        // sort the unique values
        const uniqueValues = Array.from(valueSet).sort((a, b) => a - b);

        // create items array with a select all option at the top
        const items: NumberItem[] = [
            {
                id: 'select-all',
                label: '(Alle auswählen)',
                value: NaN, // using NaN as a sentinel value for "select all"
            },
            ...uniqueValues.map((value) => ({
                id: `value-${value}`,
                label: value.toString(),
                value,
            })),
            {
                id: 'select-blanks',
                label: '(Leere auswählen)',
                value: NaN, // using NaN as a sentinel value for "select blanks"
            },
        ];

        return {
            items,
            allIds: items.map((item) => item.id),
        };
    }, [column, disabled, table.options.data]);
}
